import type { ChassisUnitLabelProps } from '../Chassis/Chassis';
import type { ChassisLayout, ChassisPort } from '../Layouts/types';
import { Alert } from '../../components/Alert/Alert';
import { IconTooltip } from '../../components/Tooltip/Tooltip';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { Chassis, ChassisUnit } from '../Chassis/Chassis';
import { ChassisPorts } from '../Layouts/ChassisPorts';
import {
  FirewallLayoutsF1,
  FirewallLayoutsF2,
  FirewallLayoutsF3,
  FirewallLayoutsMC05,
  FirewallLayoutsMC11,
} from '../Layouts/firewall/FirewallLayouts';
import { LEDs } from '../LEDs/LEDs';
import { HardwareDeviceModel, isGen1Hardware } from '../utils';

const SecurityApplianceAntennaLEDRepresentation = styled('div', {
  display: 'flex',
  width: '$16',
  height: '$12',
  backgroundColor: colors.bgPositiveLight,
  strokeAll: colors.strokePositiveLight,
  borderRadius: '$4',

  [darkThemeSelector]: {
    backgroundColor: colors.bgPositiveDark,
    strokeAll: colors.strokePositiveDark,
  },
});
SecurityApplianceAntennaLEDRepresentation.displayName = 'SecurityApplianceAntennaLEDRepresentation';

const SecurityApplianceAntennaLEDContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});
SecurityApplianceAntennaLEDContainer.displayName = 'SecurityApplianceAntennaLEDContainer';

function SecurityApplianceAntennaLED({ children }: { children: string }) {
  return (
    <SecurityApplianceAntennaLEDContainer>
      <SecurityApplianceAntennaLEDRepresentation />
      <Small weight="bold">{children}</Small>
    </SecurityApplianceAntennaLEDContainer>
  );
}

const SecurityApplianceAntennaLEDs = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
});
SecurityApplianceAntennaLEDs.displayName = 'SecurityApplianceAntennaLEDs';

const SecurityApplianceAntennaTooltip = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$10',
  widthAll: '$200',
  padding: '$8',
});
SecurityApplianceAntennaTooltip.displayName = 'SecurityApplianceAntennaTooltip';

function SecurityApplianceAntenna() {
  return (
    <IconTooltip
      tight
      side="right"
      icon="antenna"
      contents={
        <SecurityApplianceAntennaTooltip>
          <Alert
            type="inline"
            icon="information"
            variant="neutral"
            copy="Illustrative purposes only, doesn't reflect actual device status."
          />
          <SecurityApplianceAntennaLEDs>
            <Small weight="bold">Antenna LED layout</Small>
            <SecurityApplianceAntennaLED>Backend</SecurityApplianceAntennaLED>
            <SecurityApplianceAntennaLED>LTE</SecurityApplianceAntennaLED>
            <SecurityApplianceAntennaLED>WAN</SecurityApplianceAntennaLED>
            <SecurityApplianceAntennaLED>Power</SecurityApplianceAntennaLED>
          </SecurityApplianceAntennaLEDs>
        </SecurityApplianceAntennaTooltip>
      }
    />
  );
}

const FirewallMetadata = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});
FirewallMetadata.displayName = 'FirewallMetadata';

export type FirewallModel =
  | HardwareDeviceModel.Mc01
  | HardwareDeviceModel.Mc05
  | HardwareDeviceModel.Mc06
  | HardwareDeviceModel.Mc11
  | HardwareDeviceModel.F1
  | HardwareDeviceModel.F2
  | HardwareDeviceModel.F3;

// Type guard to check if a DeviceModel is a valid FirewallModel
export function isFirewallModel(deviceModel: string): deviceModel is FirewallModel {
  return [
    HardwareDeviceModel.Mc01,
    HardwareDeviceModel.Mc05,
    HardwareDeviceModel.Mc06,
    HardwareDeviceModel.Mc11,
    HardwareDeviceModel.F1,
    HardwareDeviceModel.F2,
    HardwareDeviceModel.F3,
  ].includes(deviceModel as FirewallModel);
}

// Safe conversion function from DeviceModel to FirewallModel
export function toFirewallModel(deviceModel: string): FirewallModel | null {
  return isFirewallModel(deviceModel) ? deviceModel : null;
}

export type FirewallUnit = {
  /**
   * Stable identity for the unit, used as its React key — its device UUID where there is one, so
   * reordering an HA pair doesn't reconcile one unit's ports onto the other.
   */
  id: string;
  /** The vertical pill naming the unit. */
  label: ChassisUnitLabelProps;
  /** The unit's own ports, numbered as they are on its faceplate. */
  ports: ChassisPort[];
};

export type FirewallProps = {
  model: FirewallModel;
  /**
   * One entry per physical device on the card, left to right. Most models are a single unit; the
   * ones that ship as an HA pair (see `isDualUnitFirewall`) expect two.
   */
  units: FirewallUnit[];
};

/**
 * Models that are two firewalls in one enclosure, and so are drawn as two units on one chassis.
 * Exported so callers can decide how many `units` to build — including whether to draw a placeholder
 * for a pair member that isn't configured yet.
 */
export function isDualUnitFirewall(model: FirewallModel): boolean {
  return model === HardwareDeviceModel.F3;
}

export function getFirewallLayout(model: FirewallModel): ChassisLayout {
  switch (model) {
    case HardwareDeviceModel.Mc05:
    case HardwareDeviceModel.Mc06:
      // MC05 and MC06 have the  same port layout
      return FirewallLayoutsMC05;
    case HardwareDeviceModel.Mc11:
      return FirewallLayoutsMC11;
    case HardwareDeviceModel.F1:
      return FirewallLayoutsF1;
    case HardwareDeviceModel.F2:
      return FirewallLayoutsF2;
    case HardwareDeviceModel.F3:
      return FirewallLayoutsF3;
    default:
      return FirewallLayoutsMC05;
  }
}

export function getFirewallPortOrientation(
  model: FirewallModel,
  portNumber: number,
): 'up' | 'down' | undefined {
  const layout = getFirewallLayout(model);

  for (const section of layout.sections) {
    for (const column of section.columns) {
      for (const row of column.rows) {
        if (row.ports.includes(portNumber)) {
          return row.portOrientation;
        }
      }
    }
  }

  return undefined;
}

export function Firewall({ model, units }: FirewallProps) {
  const gen1Hardware = isGen1Hardware(model);
  const layout = getFirewallLayout(model);

  return (
    <Chassis
      metadata={
        !gen1Hardware && (
          <FirewallMetadata>
            <SecurityApplianceAntenna />
          </FirewallMetadata>
        )
      }
    >
      {units.map((unit) => (
        <ChassisUnit key={unit.id} label={unit.label} leds={gen1Hardware && <LEDs />}>
          <ChassisPorts layout={layout} ports={unit.ports} />
        </ChassisUnit>
      ))}
    </Chassis>
  );
}
